import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import { useAuth } from '../hooks/useAuth';
import { toast } from '@etherisc/ui-kit';

interface GoogleOAuthButtonProps {
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  width?: string;
  onSuccess?: () => void;
  onError?: () => void;
}

export function GoogleOAuthButton({
  theme = 'outline',
  size = 'large',
  text = 'signin_with',
  shape = 'rectangular',
  width,
  onSuccess,
  onError,
}: GoogleOAuthButtonProps) {
  const { loginWithGoogle } = useAuth();
  const showError = (message: string, description?: string) => toast.error(message, { description });
  const showSuccess = (message: string, description?: string) => toast.success(message, { description });

  const handleSuccess = async (credentialResponse: CredentialResponse) => {
    try {
      if (!credentialResponse.credential) {
        throw new Error('No credential received from Google');
      }

      // Convert to our internal format
      const googleCredential = {
        credential: credentialResponse.credential,
        select_by: credentialResponse.select_by || 'user',
        clientId: credentialResponse.clientId || '',
      };

      await loginWithGoogle(googleCredential);
      showSuccess('Welcome!', 'Successfully signed in with Google');
      onSuccess?.();
    } catch (err) {
      console.error('Google OAuth success handler error:', err);
      showError('Login Failed', 'Failed to complete Google sign-in');
      onError?.();
    }
  };

  const handleError = () => {
    console.error('Google OAuth error');
    showError('Login Failed', 'Google sign-in was cancelled or failed');
    onError?.();
  };

  return (
    <div className="w-full">
      <GoogleLogin
        onSuccess={handleSuccess}
        onError={handleError}
        theme={theme}
        size={size}
        text={text}
        shape={shape}
        width={width}
        auto_select={false}
        useOneTap={false}
      />
    </div>
  );
} 